import { useContext, useEffect, useMemo, useState } from 'react';


import AddIcon from '@mui/icons-material/Add';
import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import classNames from 'classnames';
import { deleteProject, updateProject } from '../api/auth';
import { AccountContext } from '../context/account';
import { projectShape } from '../data/form-shapes';
import styles from "../styles/components/project-selector.module.scss";
import { ErrorMessage } from './error-message';
import { CreateForm } from './form-components';
import { InfoModal } from './info-modal';
import { ItemShareButton } from './project-item-share';

interface ProjectItemProps {
  onChange: (id: number | null) => void
  projectId: null | number
  compact?: boolean
  showCreateNew?: boolean
  className?: string
}

export const ProjectSelector = ({
  projectId = null,
  onChange,
  compact = false,
  showCreateNew = false,
  className,
}: ProjectItemProps) => {
  const [itemProjectId, setItemProjectId] = useState<number | string | null>(projectId)

  const {
    userProjects,
  } = useContext(AccountContext);

  useEffect(() => {
    if (itemProjectId !== "new") {
      onChange(itemProjectId === null ? null : Number(itemProjectId))
    }
  }, [itemProjectId])

  return (
    <>
      <FormControl 
        className={classNames(className, styles.formControl, {
          [styles.compact]: compact
        })}
        size={compact? 'small': "medium"}
      >
        <InputLabel id="project">Project</InputLabel>
        <Select
          label="Project"
          labelId="project"
          id="project"
          value={`${itemProjectId === null ? 0 : itemProjectId}`}
          onChange={(event: SelectChangeEvent) => {
              const { value } = event.target
              setItemProjectId(value)
            }
          }
        >
          {userProjects.map((item) => {
            const {id, name, shared_by_uid} = item
            if (shared_by_uid) {
              return <MenuItem key={id} value={id}>{name} (shared with you)</MenuItem>  
            }
            return <MenuItem key={id} value={id}>{name}</MenuItem>
          })}
          <MenuItem value={0}>Uncategorised</MenuItem>
          {showCreateNew && <MenuItem value="new"><AddIcon />Create new project</MenuItem>}
        </Select>
      </FormControl>
      {itemProjectId === "new" && 
        <CreateProjectModal 
          onSetProjectId={(id) => {
            setItemProjectId(id)
          }} />
      }
    </>
  )
}

interface ProjectNameProps {
  projectId: null | number
}
export const ProjectName = ({
  projectId,
}: ProjectNameProps) => {
  const [itemProjectId, setItemProjectId] = useState<number | null>(projectId)

  const {
    userProjects,
  } = useContext(AccountContext);
  
  const projectName = useMemo(() => {
    if (projectId) {
      const found = userProjects.find( item => {
        return item.id === projectId
      })
      if (found) {
        return found.name
      }
    }
    return "Uncategorised"
  }, [userProjects, projectId])

  return (
   <>
    <p>{projectName}</p>
   </>
  )
}

interface CreateProjectModalProps {
  onSetProjectId: (id: number | string | null) => void
}

export const CreateProjectModal = ({
  onSetProjectId,
}: CreateProjectModalProps ) => {

  const {
    userProjects,
    refetchUserProjects,
  } = useContext(AccountContext);

  
  return (
    <CreateForm
      title={"Create new project"}
      intro="You can create a new project folder to organise your purchased data points. ***You can always move your data to another folder after you have purchased your data.***"
      isModal
      shape={projectShape}
      onChange={(vals, key) => {
      }}
      successMessage={"Your project has been created."}
      defaultValues={{}}
      onCancel={() => {
        onSetProjectId(null)
      }}
      onSuccess={(response) => {
        if (response) {
          const {result} = response
          if (result && Array.isArray(result) && result.length > 0) {
            refetchUserProjects(() => {
              onSetProjectId(result[0].id)
            })
          }
        }
      }}
      onSubmit={(vals) => {
        return updateProject({
          name: vals.name,
          description: vals.description,
        })
      }}
    />
  )
}



interface EditProjectModalProps {
  projectId: number | string
  onCancel?: () => void
}

export const EditProjectModal = ({
  projectId,
  onCancel,
}: EditProjectModalProps ) => {

  const {
    userProjects,
    refetchUserProjects,
  } = useContext(AccountContext);

  const defaultValues = useMemo(() => {
    const found = userProjects.find( item => {
      return item.id === projectId
    })
    if (found) {
      return found
    }
    return {}
  }, [projectId, userProjects])

  if (!projectId) {
    return (
      <InfoModal
        open
        title={"Edit project"}
        content={<ErrorMessage message={"Project could not be found."} />}
        maxWidth="sm"
        onClose={() => {
          if (onCancel) {
            onCancel()
          }
        }}
      />
    )
  }

  return (
    <CreateForm
      title={"Edit project"}
      intro="You can update or delete your project here."
      isModal
      onCancel={() => {
        if (onCancel) {
          onCancel()
        }
      }}
      shape={projectShape}
      onChange={(vals, key) => {
      }}
      successMessage={"Your project has been updated. **When you delete project folder with items, these will be moved to Uncategorised folder.**"}
      defaultValues={defaultValues}
      onSuccess={(response) => {
        if (response) {
          const {result} = response
          if (result) {
            refetchUserProjects(() => {
              // onSetProjectId(result[0].id)
            })
          }
        }
      }}
      onDelete={() => {
        return deleteProject({
          id: projectId
        })
      }}
      onSubmit={(vals) => {
        return updateProject({
          id: projectId,
          name: vals.name,
          description: vals.description,
        })
      }}
    >
      <ItemShareButton itemId={projectId} type="project" />
    </CreateForm>
  )
}
